import React, { useCallback, useMemo, useState } from "react"
import { format, parseISO } from "date-fns"
import {
	IconBranchMergeOutline18 as MergeIcon,
	IconCubeOutline18 as ProjectIcon,
} from "nucleo-ui-outline-18"

import { IssueStatusIcon } from "@productlane/ui"

import type { ChangelogSelection, PickerItem } from "./release-picker"
import { GenerateChangelogButton } from "@/components/generate-changelog-button"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { resolveLead, toPickerItems } from "./release-picker"
import { ReleasePickerDialog } from "./release-picker-dialog"

const PREVIEW_COUNT = 3

/* The empty changelog's way in: what shipped since the last release, at a
   glance, and one button into the picker. It shares the picker's default
   query, so opening the picker afterwards reads from the cache. */
export const ShippedSinceCard = React.memo(
	({
		canFetch,
		linearConnected,
		generationsLeft,
		onGenerate,
	}: {
		canFetch: boolean
		linearConnected: boolean
		generationsLeft: number
		onGenerate: (selection: ChangelogSelection) => void
	}) => {
		// Open state lives here, not in the editor, so opening the picker
		// re-renders this card and the dialog only.
		const [isPickerOpen, setIsPickerOpen] = useState(false)
		const onChoose = useCallback(() => {
			// The plan and Linear checks run in the button; the quota is checked
			// before the picker opens so nobody picks a release that can't be written.
			if (generationsLeft <= 0) {
				toast.error({
					title: "AI Changelog Quota Exceeded",
					description:
						"You've reached your daily AI changelog generation limit (20 per day). Try again tomorrow.",
				})
				return
			}
			setIsPickerOpen(true)
		}, [generationsLeft])

		const candidates = api.linear.changelogCandidates.useQuery(
			{ since: null, until: null },
			{ enabled: canFetch, staleTime: 60_000 },
		)
		const items = useMemo(
			() => (candidates.data ? toPickerItems(candidates.data) : []),
			[candidates.data],
		)
		const customerFacing = items.filter((item) => !item.internal)
		const preview = useMemo(() => {
			const rest = new Set(customerFacing.map((item) => item.key))
			const picked: PickerItem[] = []
			while (picked.length < PREVIEW_COUNT) {
				const next = resolveLead(customerFacing, rest, null)
				if (!next) break
				picked.push(next)
				rest.delete(next.key)
			}
			return picked
		}, [customerFacing])

		const lastReleaseAt = candidates.data?.lastReleaseAt
		const since = lastReleaseAt
			? `since your ${format(parseISO(lastReleaseAt), "MMM d")} release`
			: "in the last 3 months"
		const title = !canFetch
			? "Generate changelog"
			: candidates.data
				? customerFacing.length === 0
					? `Nothing customer-facing released ${since}`
					: `${customerFacing.length} ${customerFacing.length === 1 ? "change" : "changes"} released ${since}`
				: "Reading what was released..."
		const moreCount = customerFacing.length - preview.length

		return (
			<div className="mt-6 w-full rounded-lg border border-gray-200 dark:border-gray-700/80">
				<div className="px-4 pt-3.5">
					<p className="text-[13px] font-medium text-primary">{title}</p>
					<p className="mt-0.5 text-[13px] text-secondary">
						Choose the issues, projects, and pull requests to include.
					</p>
				</div>
				{canFetch && (
					// Fixed height, so the card keeps its size while the list loads.
					<ul className="mt-2 h-32 px-2">
						{candidates.data
							? preview.map((item) => (
									<li
										key={item.key}
										className="flex h-8 items-center gap-2.5 px-2 text-[13px]"
									>
										<KindIcon kind={item.kind} />
										<span className="min-w-0 flex-1 truncate text-primary">
											{item.title}
										</span>
										{item.requestCount > 0 && (
											<span className="flex-none text-xs tabular-nums text-tertiary">
												{item.requestCount}{" "}
												{item.requestCount === 1 ? "request" : "requests"}
											</span>
										)}
									</li>
								))
							: Array.from({ length: PREVIEW_COUNT }, (_, index) => (
									<li
										key={index}
										className="flex h-8 items-center gap-2.5 px-2"
									>
										<span className="h-4 w-4 flex-none rounded bg-gray-100 dark:bg-gray-800" />
										<span className="h-2.5 w-1/2 rounded bg-gray-100 dark:bg-gray-800" />
									</li>
								))}
						{candidates.data && moreCount > 0 && (
							<li>
								<button
									type="button"
									onClick={onChoose}
									className="flex h-8 w-full items-center gap-2.5 rounded-md px-2 text-left text-[13px] text-secondary hover:bg-gray-100 hover:text-primary dark:hover:bg-gray-800"
								>
									<span className="w-4 flex-none" />
									Show {moreCount} more
								</button>
							</li>
						)}
					</ul>
				)}
				<div className="flex items-center gap-2 px-4 pb-3.5 pt-3">
					<GenerateChangelogButton
						onClick={onChoose}
						label="Select items"
						tooltipLabel="Pick what was released, then write the post from it."
						workspaceLinearConnected={linearConnected}
						variant="cta"
					/>
				</div>
				<ReleasePickerDialog
					isOpen={isPickerOpen}
					onOpenChange={setIsPickerOpen}
					onGenerate={(selection) => {
						setIsPickerOpen(false)
						onGenerate(selection)
					}}
				/>
			</div>
		)
	},
)

// One 16px slot for every kind, so the titles start on the same line.
export function KindIcon({ kind }: { kind: PickerItem["kind"] }) {
	return (
		<span className="flex h-4 w-4 flex-none items-center justify-center">
			{kind === "project" ? (
				<ProjectIcon className="h-3.5 w-3.5 flex-none text-secondary" />
			) : kind === "issue" ? (
				<IssueStatusIcon
					status="completed"
					color="#5E6AD2"
					className="h-3.5 w-3.5 flex-none"
				/>
			) : (
				<MergeIcon className="h-3.5 w-3.5 flex-none text-secondary" />
			)}
		</span>
	)
}
